import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import {
  ComplianceMatrixEntry,
  ComplianceMatrixMapping,
  ComplianceMatrixEvidence,
  ComplianceMatrixAssessment,
  ComplianceMatrixStatusHistory,
  ComplianceMatrixStatus,
  MatrixImplementationStatus,
  MappingCategory,
  MappingStrength,
  EvidenceType,
  AssessmentMethod
} from './types';
import { matrixService } from './matrixService';

interface Props {
  entry: ComplianceMatrixEntry;
  onClose: () => void;
  onRefresh: () => void;
}

export const ComplianceMatrixDetailModal: React.FC<Props> = ({ entry, onClose, onRefresh }) => {
  const [activeTab, setActiveTab] = useState<'OVERVIEW' | 'MAPPINGS' | 'EVIDENCES' | 'ASSESSMENTS' | 'HISTORY'>('OVERVIEW');
  const [mappings, setMappings] = useState<ComplianceMatrixMapping[]>([]);
  const [evidences, setEvidences] = useState<ComplianceMatrixEvidence[]>([]);
  const [assessments, setAssessments] = useState<ComplianceMatrixAssessment[]>([]);
  const [history, setHistory] = useState<ComplianceMatrixStatusHistory[]>([]);
  const [loading, setLoading] = useState(false);

  // Forms
  const [showAddMapping, setShowAddMapping] = useState(false);
  const [mappingCategory, setMappingCategory] = useState<MappingCategory>('OBLIGATION');
  const [mappingTargetId, setMappingTargetId] = useState('');
  const [mappingTargetTitle, setMappingTargetTitle] = useState('');
  const [mappingStrength, setMappingStrength] = useState<MappingStrength>('ADEQUATE');

  const [showAddEvidence, setShowAddEvidence] = useState(false);
  const [evidenceTitle, setEvidenceTitle] = useState('');
  const [evidenceType, setEvidenceType] = useState<EvidenceType>('RECORD');
  const [evidenceFileName, setEvidenceFileName] = useState('');
  const [evidenceValidFrom, setEvidenceValidFrom] = useState(new Date().toISOString().slice(0, 10));
  const [evidenceValidUntil, setEvidenceValidUntil] = useState('');

  const [showAddAssessment, setShowAddAssessment] = useState(false);
  const [asmtMethod, setAsmtMethod] = useState<AssessmentMethod>('DESK_REVIEW');
  const [asmtStatus, setAsmtStatus] = useState<ComplianceMatrixStatus>('COMPLIANT');
  const [asmtJustification, setAsmtJustification] = useState('');
  const [asmtGap, setAsmtGap] = useState('');
  const [asmtFindingReq, setAsmtFindingReq] = useState(false);
  const [asmtCapaReq, setAsmtCapaReq] = useState(false);

  const loadTabData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'MAPPINGS') {
        const data = await matrixService.getMappings(entry.id);
        setMappings(data);
      } else if (activeTab === 'EVIDENCES') {
        const data = await matrixService.getEvidences(entry.id);
        setEvidences(data);
      } else if (activeTab === 'ASSESSMENTS') {
        const data = await matrixService.getAssessments(entry.id);
        setAssessments(data);
      } else if (activeTab === 'HISTORY') {
        const data = await matrixService.getStatusHistory(entry.id);
        setHistory(data);
      }
    } catch (err: any) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTabData();
  }, [activeTab, entry.id]);

  const handleAddMappingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await matrixService.addMapping(entry.id, {
        mapping_category: mappingCategory,
        target_reference_id: mappingTargetId,
        target_title: mappingTargetTitle,
        mapping_strength: mappingStrength,
        created_by: 'Compliance Auditor'
      });
      setShowAddMapping(false);
      setMappingTargetId('');
      setMappingTargetTitle('');
      loadTabData();
      onRefresh();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleDeleteMapping = async (mapId: string) => {
    if (!confirm('Are you sure you want to remove this mapping?')) return;
    try {
      await matrixService.deleteMapping(entry.id, mapId);
      loadTabData();
      onRefresh();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleAddEvidenceSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await matrixService.addEvidence(entry.id, {
        title: evidenceTitle,
        evidence_type: evidenceType,
        file_name: evidenceFileName,
        file_path: `/evidence/${evidenceFileName}`,
        file_size_bytes: 1024 * 1024,
        valid_from: evidenceValidFrom,
        valid_until: evidenceValidUntil || undefined,
        uploaded_by: 'Compliance Auditor'
      });
      setShowAddEvidence(false);
      setEvidenceTitle('');
      setEvidenceFileName('');
      loadTabData();
      onRefresh();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleAddAssessmentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await matrixService.addAssessment(entry.id, {
        assessment_method: asmtMethod,
        assigned_status: asmtStatus,
        compliance_justification: asmtJustification,
        gap_analysis: asmtGap || undefined,
        is_finding_required: asmtFindingReq,
        is_capa_required: asmtCapaReq,
        assessor_name: 'Lead Compliance Auditor',
        assessor_role: 'Auditor'
      });
      setShowAddAssessment(false);
      setAsmtJustification('');
      setAsmtGap('');
      loadTabData();
      onRefresh();
    } catch (err: any) {
      alert(err.message);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-card modal-xl" style={{ height: '92vh' }}>
        {/* Header */}
        <div className="modal-header">
          <div className="modal-header-content">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <span style={{ fontSize: '13px', fontWeight: 700, padding: '2px 8px', borderRadius: '4px', background: '#dbeafe', color: '#1e40af', fontFamily: 'monospace' }}>
                {entry.matrix_code}
              </span>
              <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '4px', background: '#ecfdf5', color: '#065f46' }}>
                {entry.compliance_status}
              </span>
            </div>
            <h3>{entry.requirement_identifier} — {entry.requirement_title}</h3>
            <p>Compliance Matrix verification, operational mapping, and evidence dossier</p>
          </div>
          <button type="button" onClick={onClose} className="modal-close-btn" title="Kapat" aria-label="Kapat">
            <X size={20} />
          </button>
        </div>

        {/* Tab Navigation */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border-color, #e2e8f0)', background: 'var(--bg-subtle, #f8fafc)', padding: '0 20px', gap: '8px' }}>
          {(['OVERVIEW', 'MAPPINGS', 'EVIDENCES', 'ASSESSMENTS', 'HISTORY'] as const).map((tab) => {
            const labels: Record<string, string> = {
              OVERVIEW: 'Overview & Implementation',
              MAPPINGS: `Cross Mappings (${entry.mappings_count || 0})`,
              EVIDENCES: `Evidence Records (${entry.evidences_count || 0})`,
              ASSESSMENTS: `Assessments (${entry.assessments_count || 0})`,
              HISTORY: 'Status History',
            };
            const isActive = activeTab === tab;
            return (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                style={{
                  padding: '12px 16px',
                  fontSize: '13px',
                  fontWeight: isActive ? 600 : 500,
                  color: isActive ? 'var(--primary-color, #2563eb)' : 'var(--text-muted, #64748b)',
                  borderBottom: isActive ? '2px solid var(--primary-color, #2563eb)' : '2px solid transparent',
                  background: 'transparent',
                  borderTop: 'none',
                  borderLeft: 'none',
                  borderRight: 'none',
                  cursor: 'pointer',
                }}
              >
                {labels[tab]}
              </button>
            );
          })}
        </div>

        {/* Content Body */}
        <div className="modal-body">
          {activeTab === 'OVERVIEW' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', background: 'var(--bg-subtle, #f8fafc)', padding: '16px', borderRadius: '8px', border: '1px solid var(--border-color, #e2e8f0)' }}>
                <div>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted, #64748b)', display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Responsible Department</span>
                  <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary, #0f172a)' }}>{entry.responsible_department}</span>
                </div>
                <div>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted, #64748b)', display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Lead Owner</span>
                  <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary, #0f172a)' }}>{entry.lead_owner_name}</span>
                </div>
                <div>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted, #64748b)', display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Implementation Status</span>
                  <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary, #0f172a)' }}>{entry.implementation_status}</span>
                </div>
                <div>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted, #64748b)', display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Last Assessment Date</span>
                  <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary, #0f172a)' }}>{entry.last_assessed_at || 'Not Assessed'}</span>
                </div>
                <div>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted, #64748b)', display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Mapping Coverage</span>
                  <span style={{ fontSize: '14px', fontWeight: 700, color: '#2563eb' }}>{entry.mapping_coverage_score}%</span>
                </div>
                <div>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted, #64748b)', display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Evidence Coverage</span>
                  <span style={{ fontSize: '14px', fontWeight: 700, color: '#2563eb' }}>{entry.evidence_coverage_score}%</span>
                </div>
              </div>

              <div>
                <h4 style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary, #0f172a)', margin: '0 0 6px 0' }}>Implementation Summary</h4>
                <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary, #334155)', background: 'var(--card-bg, #fff)', padding: '12px', borderRadius: '6px', border: '1px solid var(--border-color, #e2e8f0)', lineHeight: 1.5 }}>
                  {entry.implementation_summary}
                </p>
              </div>

              {entry.gap_description && (
                <div>
                  <h4 style={{ fontSize: '13px', fontWeight: 600, color: '#991b1b', margin: '0 0 6px 0' }}>Identified Gap Analysis</h4>
                  <p style={{ margin: 0, fontSize: '13px', color: '#7f1d1d', background: '#fef2f2', padding: '12px', borderRadius: '6px', border: '1px solid #fecaca', lineHeight: 1.5 }}>
                    {entry.gap_description}
                  </p>
                </div>
              )}

              {entry.is_reassessment_required && (
                <div style={{ padding: '12px 16px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '6px', fontSize: '12px', color: '#92400e' }}>
                  <strong>⚠️ Reassessment Required:</strong> {entry.reassessment_trigger_reason || 'Evidence validity expiry or regulatory update requires formal review.'}
                </div>
              )}
            </div>
          )}

          {activeTab === 'MAPPINGS' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <p style={{ fontSize: '12px', color: 'var(--text-muted, #64748b)', margin: 0 }}>
                  Associated obligations, procedures, SOPs, audit checklists, or CAPA items linked to this requirement.
                </p>
                <button
                  type="button"
                  onClick={() => setShowAddMapping(!showAddMapping)}
                  className="btn-primary"
                  style={{ fontSize: '12px', padding: '6px 12px' }}
                >
                  {showAddMapping ? 'Cancel' : '+ Add Mapping'}
                </button>
              </div>

              {showAddMapping && (
                <form onSubmit={handleAddMappingSubmit} style={{ padding: '16px', background: 'var(--bg-subtle, #f8fafc)', borderRadius: '8px', border: '1px solid var(--border-color, #e2e8f0)', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div className="form-grid-2">
                    <div className="form-field">
                      <label>Mapping Category</label>
                      <select
                        value={mappingCategory}
                        onChange={(e) => setMappingCategory(e.target.value as MappingCategory)}
                      >
                        <option value="OBLIGATION">Compliance Obligation</option>
                        <option value="SOP_DOCUMENT">SOP / Manual Procedure</option>
                        <option value="AUDIT_PROGRAMME">Audit Programme</option>
                        <option value="INSPECTION_CHECKLIST">Inspection Checklist</option>
                        <option value="RISK_BARRIER">Risk Barrier / Control</option>
                        <option value="CAPA_ACTION">CAPA Corrective Action</option>
                      </select>
                    </div>
                    <div className="form-field">
                      <label>Control Strength</label>
                      <select
                        value={mappingStrength}
                        onChange={(e) => setMappingStrength(e.target.value as MappingStrength)}
                      >
                        <option value="STRONG">Strong</option>
                        <option value="ADEQUATE">Adequate</option>
                        <option value="WEAK">Weak</option>
                        <option value="DEFICIENT">Deficient</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-grid-2">
                    <div className="form-field">
                      <label>Target Reference ID</label>
                      <input
                        type="text"
                        placeholder="e.g. OBL-001 or OM-A-SEC-1"
                        value={mappingTargetId}
                        onChange={(e) => setMappingTargetId(e.target.value)}
                        required
                      />
                    </div>
                    <div className="form-field">
                      <label>Target Title</label>
                      <input
                        type="text"
                        placeholder="e.g. Annual SMS Review Procedure"
                        value={mappingTargetTitle}
                        onChange={(e) => setMappingTargetTitle(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '4px' }}>
                    <button type="submit" className="btn-primary">
                      Save Mapping
                    </button>
                  </div>
                </form>
              )}

              {loading ? (
                <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-muted, #64748b)', fontSize: '13px' }}>Loading mappings...</div>
              ) : mappings.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-muted, #64748b)', fontSize: '13px' }}>No mappings defined yet.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {mappings.map((m) => (
                    <div key={m.id} style={{ padding: '12px 16px', background: 'var(--card-bg, #fff)', border: '1px solid var(--border-color, #e2e8f0)', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px' }}>
                      <div>
                        <div style={{ fontWeight: 600, color: 'var(--text-primary, #0f172a)' }}>{m.target_title}</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-muted, #64748b)', marginTop: '2px' }}>
                          {m.mapping_category} • Ref: {m.target_reference_id} • Strength: <span style={{ fontWeight: 600, color: '#2563eb' }}>{m.mapping_strength}</span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleDeleteMapping(m.id)}
                        style={{ background: 'none', border: 'none', color: '#ef4444', fontWeight: 600, cursor: 'pointer', fontSize: '12px' }}
                      >
                        Delete
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'EVIDENCES' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <p style={{ fontSize: '12px', color: 'var(--text-muted, #64748b)', margin: 0 }}>Verified compliance evidence, records, and certification documentation.</p>
                <button
                  type="button"
                  onClick={() => setShowAddEvidence(!showAddEvidence)}
                  className="btn-primary"
                  style={{ fontSize: '12px', padding: '6px 12px' }}
                >
                  {showAddEvidence ? 'Cancel' : '+ Add Evidence'}
                </button>
              </div>

              {showAddEvidence && (
                <form onSubmit={handleAddEvidenceSubmit} style={{ padding: '16px', background: 'var(--bg-subtle, #f8fafc)', borderRadius: '8px', border: '1px solid var(--border-color, #e2e8f0)', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div className="form-grid-2">
                    <div className="form-field">
                      <label>Evidence Title</label>
                      <input
                        type="text"
                        value={evidenceTitle}
                        onChange={(e) => setEvidenceTitle(e.target.value)}
                        required
                      />
                    </div>
                    <div className="form-field">
                      <label>Evidence Type</label>
                      <select
                        value={evidenceType}
                        onChange={(e) => setEvidenceType(e.target.value as EvidenceType)}
                      >
                        <option value="RECORD">Operational Record</option>
                        <option value="CERTIFICATE">Certificate / Authorization</option>
                        <option value="AUDIT_REPORT">Audit Report</option>
                        <option value="PROCEDURE">Approved Procedure</option>
                        <option value="TRAINING_LOG">Training Log</option>
                        <option value="SYSTEM_EXPORT">System Export Data</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-grid-3">
                    <div className="form-field">
                      <label>File Name</label>
                      <input
                        type="text"
                        placeholder="evidence.pdf"
                        value={evidenceFileName}
                        onChange={(e) => setEvidenceFileName(e.target.value)}
                        required
                      />
                    </div>
                    <div className="form-field">
                      <label>Valid From</label>
                      <input
                        type="date"
                        value={evidenceValidFrom}
                        onChange={(e) => setEvidenceValidFrom(e.target.value)}
                        required
                      />
                    </div>
                    <div className="form-field">
                      <label>Valid Until (Optional)</label>
                      <input
                        type="date"
                        value={evidenceValidUntil}
                        onChange={(e) => setEvidenceValidUntil(e.target.value)}
                      />
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '4px' }}>
                    <button type="submit" className="btn-primary">
                      Save Evidence
                    </button>
                  </div>
                </form>
              )}

              {loading ? (
                <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-muted, #64748b)', fontSize: '13px' }}>Loading evidences...</div>
              ) : evidences.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-muted, #64748b)', fontSize: '13px' }}>No evidence records uploaded.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {evidences.map((evi) => (
                    <div key={evi.id} style={{ padding: '12px 16px', background: 'var(--card-bg, #fff)', border: '1px solid var(--border-color, #e2e8f0)', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px' }}>
                      <div>
                        <div style={{ fontWeight: 600, color: 'var(--text-primary, #0f172a)' }}>{evi.title}</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-muted, #64748b)', marginTop: '2px' }}>
                          {evi.evidence_type} • {evi.file_name} • Validity: {evi.valid_from} - {evi.valid_until || 'Permanent'}
                        </div>
                      </div>
                      <div>
                        {evi.is_expired ? (
                          <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '4px', background: '#fee2e2', color: '#991b1b' }}>Expired</span>
                        ) : (
                          <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '4px', background: '#ecfdf5', color: '#065f46' }}>Valid</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'ASSESSMENTS' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <p style={{ fontSize: '12px', color: 'var(--text-muted, #64748b)', margin: 0 }}>Official periodic compliance assessments and assigned status verdicts.</p>
                <button
                  type="button"
                  onClick={() => setShowAddAssessment(!showAddAssessment)}
                  className="btn-primary"
                  style={{ fontSize: '12px', padding: '6px 12px' }}
                >
                  {showAddAssessment ? 'Cancel' : '+ Record Assessment'}
                </button>
              </div>

              {showAddAssessment && (
                <form onSubmit={handleAddAssessmentSubmit} style={{ padding: '16px', background: 'var(--bg-subtle, #f8fafc)', borderRadius: '8px', border: '1px solid var(--border-color, #e2e8f0)', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div className="form-grid-2">
                    <div className="form-field">
                      <label>Assessment Method</label>
                      <select
                        value={asmtMethod}
                        onChange={(e) => setAsmtMethod(e.target.value as AssessmentMethod)}
                      >
                        <option value="DESK_REVIEW">Desk Review</option>
                        <option value="AUDIT_SAMPLE">Audit Sample</option>
                        <option value="ON_SITE_OBSERVATION">On-Site Observation</option>
                        <option value="INTERVIEW">Interview</option>
                        <option value="SYSTEM_TRACE">System Trace Verification</option>
                      </select>
                    </div>
                    <div className="form-field">
                      <label>Assigned Compliance Status</label>
                      <select
                        value={asmtStatus}
                        onChange={(e) => setAsmtStatus(e.target.value as ComplianceMatrixStatus)}
                      >
                        <option value="COMPLIANT">COMPLIANT</option>
                        <option value="PARTIALLY_COMPLIANT">PARTIALLY COMPLIANT</option>
                        <option value="NON_COMPLIANT">NON COMPLIANT</option>
                        <option value="EXEMPTED">EXEMPTED</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-field">
                    <label>Compliance Justification</label>
                    <textarea
                      value={asmtJustification}
                      onChange={(e) => setAsmtJustification(e.target.value)}
                      rows={2}
                      required
                    />
                  </div>

                  <div className="form-field">
                    <label>Gap Analysis (If Applicable)</label>
                    <textarea
                      value={asmtGap}
                      onChange={(e) => setAsmtGap(e.target.value)}
                      rows={2}
                    />
                  </div>

                  <div style={{ display: 'flex', gap: '16px', fontSize: '12px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={asmtFindingReq}
                        onChange={(e) => setAsmtFindingReq(e.target.checked)}
                      />
                      Findings Record Required
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={asmtCapaReq}
                        onChange={(e) => setAsmtCapaReq(e.target.checked)}
                      />
                      CAPA Action Required
                    </label>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '4px' }}>
                    <button type="submit" className="btn-primary">
                      Save Assessment Decision
                    </button>
                  </div>
                </form>
              )}

              {loading ? (
                <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-muted, #64748b)', fontSize: '13px' }}>Loading assessments...</div>
              ) : assessments.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-muted, #64748b)', fontSize: '13px' }}>No assessment history recorded.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {assessments.map((a) => (
                    <div key={a.id} style={{ padding: '14px', background: 'var(--card-bg, #fff)', border: '1px solid var(--border-color, #e2e8f0)', borderRadius: '8px', display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '13px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: 700, color: 'var(--text-primary, #0f172a)' }}>{a.assessor_name} ({a.assessor_role})</span>
                        <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '4px', background: '#dbeafe', color: '#1e40af' }}>{a.assigned_status}</span>
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted, #64748b)' }}>
                        {a.assessment_method} • Date: {a.assessment_date}
                      </div>
                      <div style={{ background: 'var(--bg-subtle, #f8fafc)', padding: '8px 12px', borderRadius: '4px', marginTop: '4px' }}>
                        <strong>Justification:</strong> {a.compliance_justification}
                      </div>
                      {a.gap_analysis && (
                        <div style={{ color: '#991b1b', background: '#fef2f2', padding: '8px 12px', borderRadius: '4px' }}>
                          <strong>Gap Analysis:</strong> {a.gap_analysis}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'HISTORY' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <p style={{ fontSize: '12px', color: 'var(--text-muted, #64748b)', margin: 0 }}>Compliance status lifecycle change history and immutable audit trail.</p>
              {loading ? (
                <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-muted, #64748b)', fontSize: '13px' }}>Loading history...</div>
              ) : history.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-muted, #64748b)', fontSize: '13px' }}>No history records found.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {history.map((h) => (
                    <div key={h.id} style={{ padding: '12px 16px', background: 'var(--card-bg, #fff)', border: '1px solid var(--border-color, #e2e8f0)', borderRadius: '8px', fontSize: '13px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--text-secondary, #334155)', fontSize: '12px' }}>
                        <span>{h.from_status} → <strong style={{ color: '#2563eb' }}>{h.to_status}</strong></span>
                        <span style={{ color: 'var(--text-muted, #64748b)' }}>{h.changed_at}</span>
                      </div>
                      <div style={{ marginTop: '4px', color: 'var(--text-primary, #0f172a)' }}>{h.reason}</div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted, #64748b)', marginTop: '2px' }}>Changed by: {h.changed_by}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button type="button" onClick={onClose} className="btn-secondary">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
